/* eslint-disable jsx-a11y/anchor-has-content */
import React from "react";
import useMediaQuery from "@restart/hooks/cjs/useMediaQuery";
import MultiCarousel from "react-multi-carousel";
import { BiChevronLeft } from "@react-icons/all-files/bi/BiChevronLeft";
import { BiChevronRight } from "@react-icons/all-files/bi/BiChevronRight";
import { classNames } from "repoV2/utils/common/render/classNames";
import { SECTION_IDS } from "@Utils";
import { IHost } from "@Interfaces";
import { Navbar } from "@Modules/common";
import { ITemplate } from "@Templates/ITemplate";
import { FooterSection } from "repoV2/features/CreatorPages/modules/FooterSection";
import style from "../root/classic.module.scss";
import { GalleryPlaceholder, GalleryCard } from "../components";
import AboutCover from "../components/AboutCover";

// TODO - @aditya: make common of this and root index (pick in phase2)
const ButtonGroup = ({
    next,
    previous,
    postAnalyticalEvent,
    isCarouselNeeded = true,
}: any): JSX.Element | null =>
    isCarouselNeeded ? (
        <div className={style.arrowGroup}>
            <div
                className={`${style.arrowStyleCarousal} ${style.leftAligned}`}
                onClick={() => {
                    previous();
                    postAnalyticalEvent({
                        event_name: "scrollLeft",
                        action: "scroll",
                        ui_component: "Button",
                    });
                }}
            >
                <BiChevronLeft />
            </div>
            <div
                className={`${style.arrowStyleCarousal} ${style.rightAligned}`}
                onClick={() => {
                    next();
                    postAnalyticalEvent({
                        event_name: "scrollRight",
                        action: "scroll",
                        ui_component: "Button",
                    });
                }}
            >
                <BiChevronRight />
            </div>
        </div>
    ) : null;

const responsive = {
    desktop: {
        breakpoint: { max: 3000, min: 1440 },
        items: 3,
    },
    tablet: {
        breakpoint: { max: 1439, min: 769 },
        items: 2,
    },
    mobile: {
        breakpoint: { max: 768, min: 0 },
        items: 1,
    },
};

const Classic = ({
    handleSocialShare,
    handlePreviewPopup,
    postAnalyticalEvent,
    preview,

    // common to be used by sub-components in TemplateRoots
    hostData,
    navbarProps,
    aboutSectionProps,
    gallerySectionProps,
    footerSectionProps,
}: ITemplate.IProps): JSX.Element => {
    const {
        hideComponent: hideGallerySection,
        title: gallerySectionTitle,
        galleryData,
        pauseGalleryAutoplay,
        onGalleryItemClick,
        onGalleryItemShareClick,
    } = gallerySectionProps;

    // Whether display viewport is desktop
    const isDesktop: boolean = useMediaQuery(
        `(min-width: ${responsive.desktop.breakpoint.min}px)`
    );

    // Whether display viewport is tablet (assumes isDesktop = false)
    const isTablet: boolean = useMediaQuery(
        `(min-width: ${responsive.tablet.breakpoint.min}px)`
    );

    const device: keyof typeof responsive =
        (isDesktop && "desktop") || (isTablet && "tablet") || "mobile";

    // Carousel will only be needed if the total length of the gallery is greater than the number of it
    const isCarouselNeeded: boolean =
        galleryData.length > responsive[device].items;

    return (
        <div className={style.root}>
            <Navbar {...navbarProps} />
            <div className={style.hostIntro}>
                <AboutCover {...aboutSectionProps} />
            </div>
            {(!hideGallerySection && galleryData.length > 0 && (
                <div
                    className={style.galleryContainer}
                    id={SECTION_IDS.gallery}
                >
                    <a
                        id={SECTION_IDS.gallery}
                        className={style.anchorGallery}
                    />
                    <div className={style.galleryHeader}>
                        {gallerySectionTitle}
                    </div>
                    <div className={style.galleryOuterDiv}>
                        <MultiCarousel
                            responsive={responsive}
                            ssr
                            arrows={false}
                            infinite
                            className={classNames(
                                style.galleryC,
                                isCarouselNeeded && style.carouselEnabled
                            )}
                            deviceType={device}
                            autoPlay={!pauseGalleryAutoplay && isCarouselNeeded}
                            swipeable={isCarouselNeeded}
                            draggable={isCarouselNeeded}
                            customButtonGroup={
                                // eslint-disable-next-line react/jsx-wrap-multilines
                                <ButtonGroup
                                    isCarouselNeeded={isCarouselNeeded}
                                    postAnalyticalEvent={postAnalyticalEvent}
                                />
                            }
                        >
                            {galleryData.map((item: IHost.IGalleryItem) => (
                                <GalleryCard
                                    key={item.uuid}
                                    source={item.media}
                                    type={item.type}
                                    title={item.caption}
                                    onClick={() => {
                                        onGalleryItemClick(item);
                                    }}
                                    onClickShare={() => {
                                        onGalleryItemShareClick(item);
                                    }}
                                />
                            ))}
                        </MultiCarousel>
                    </div>
                </div>
            )) ||
                (preview && (
                    <>
                        <a
                            id={SECTION_IDS.gallery}
                            className={style.anchorGallery}
                        />
                        <div className={style.galleryHeader}>
                            {gallerySectionTitle}
                        </div>
                        <div className={style.previewSection}>
                            {[1, 2, 3].map(item => (
                                <GalleryPlaceholder
                                    key={item}
                                    item={item}
                                    onClick={() => handlePreviewPopup()}
                                />
                            ))}
                        </div>
                    </>
                )) ||
                null}
            <FooterSection {...footerSectionProps} />
            <div className={style.stickyContainer}>
                <button
                    type="button"
                    className={style.spreadWordButton}
                    onClick={() => {
                        if (hostData.custom_bottom_button_cta) {
                            window.location.href =
                                hostData.custom_bottom_button_cta;
                        } else {
                            handleSocialShare("spread");
                        }
                    }}
                >
                    {hostData?.custom_bottom_button_text || "SPREAD THE WORD"}
                </button>
            </div>
        </div>
    );
};

export default Classic;
